import { useAudioTime } from "@aircall/tractor";
import { useMemo } from "react";

import { useCall } from "@/hooks/useCall/useCall";
import { ActiveUtterances } from "../ScrollManagerContext.decl";

/**
 * The hook responsible for creating a map which defines for each utterance its active range time.
 * The hook will make sure that for each current time at least one utterance will be active.
 * In order to fill the gaps between the utterances, each utterance will be active once its previous one finished.
 * Edge cases - for the first utterance we will set 0 as start time and for the last one we will set Infinity.
 *
 * example,
 * input: [{startTime: 1.3, endTime: 1.6}, {startTime: 1.8, endTime: 2.2}]
 * output: [{start: 0, end: 1.6}, {start: 1.6, end: Infinity}]
 */
const useUtterancesRange = () => {
  const { call } = useCall();

  return useMemo(() => {
    if (!call?.utterances?.length) {
      return [];
    }

    return call.utterances.map((utterance, index) => {
      const start =
        index === 0
          ? 0
          : Math.min(utterance.startTime, call.utterances![index - 1].endTime);
      const end =
        index === (call?.utterances?.length ?? 0) - 1
          ? Infinity
          : utterance.endTime;

      return { start, end };
    });
  }, [call]);
};

/**
 * The hook responsible for finding all the active utterances and to return an array of all their indexes.
 * Inlined into the memo so playback ticks do not allocate an options object per utterance.
 */
export const useActiveUtterances = (): ActiveUtterances => {
  const { currentTime } = useAudioTime();
  const utterancesRange = useUtterancesRange();

  return useMemo(() => {
    const active: number[] = [];

    for (let index = 0; index < utterancesRange.length; index += 1) {
      const utter = utterancesRange[index];
      if (currentTime >= utter.start && currentTime <= utter.end) {
        active.push(index);
      }
    }

    return active;
  }, [currentTime, utterancesRange]);
};
